import React, { useState, useEffect } from 'react';
import { collection, getDocs, setDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface Secret {
  id: string;
  key: string;
  value: string;
  description: string;
  category: string;
  updatedAt?: any;
  updatedBy?: string;
  isSystemDefault?: boolean;
  isOverridden?: boolean;
  isActive?: boolean;
}

const CATEGORIES = [
  { id: 'aws', name: 'AWS Credentials', icon: '☁️' },
  { id: 'stripe', name: 'Stripe Payments', icon: '💳' },
  { id: 'email', name: 'Email Marketing', icon: '📧' },
  { id: 'google', name: 'Google Cloud', icon: '🔍' },
  { id: 'other', name: 'Other Integrations', icon: '🔌' },
];

const AdminSecretsManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isDark } = useAdminTheme();
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSecret, setEditingSecret] = useState<Partial<Secret> | null>(null);
  const [showValues, setShowValues] = useState<{ [key: string]: boolean }>({});

  const fetchSecrets = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. Fetch Overrides from Firestore
      const secretsRef = collection(db, 'system_secrets');
      const q = query(secretsRef, orderBy('category'), orderBy('key'));
      const snapshot = await getDocs(q);
      const overrides = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isSystemDefault: false,
        isOverridden: false, // Will be set during merge
        isActive: true
      })) as Secret[];

      // 2. Fetch System Defaults from Cloud Function
      let systemDefaults: Secret[] = [];
      try {
        const functions = getFunctions();
        const getSystemSecretsFn = httpsCallable(functions, 'getSystemSecrets');
        const result = await getSystemSecretsFn();
        if (result.data && (result.data as any).success) {
          systemDefaults = (result.data as any).secrets.map((s: any) => ({
            ...s,
            id: `sys_${s.key}`,
            isActive: false // Will be set during merge
          }));
        }
      } catch (err) {
        console.warn('Could not fetch system defaults:', err);
      }

      // 3. Merge Data
      const mergedSecrets: Secret[] = [...overrides];
      
      systemDefaults.forEach(sysSecret => {
        const override = overrides.find(o => o.key === sysSecret.key);
        if (override) {
          // Mark system default as overridden
          mergedSecrets.push({
            ...sysSecret,
            isOverridden: true,
            isActive: false
          });
          // Mark override as active
          override.isActive = true;
          override.isOverridden = false; // It IS the override
        } else {
          // No override, system default is active
          mergedSecrets.push({
            ...sysSecret,
            isOverridden: false,
            isActive: true
          });
        }
      });

      // Sort by category then key
      mergedSecrets.sort((a, b) => {
        if (a.category !== b.category) return a.category.localeCompare(b.category);
        return a.key.localeCompare(b.key);
      });

      setSecrets(mergedSecrets);
    } catch (error) {
      console.error('Error fetching secrets:', error);
      showError('Failed to load system secrets');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchSecrets();
  }, [fetchSecrets]);

  const handleSave = async () => {
    if (!editingSecret?.key || !editingSecret?.value || !editingSecret?.category) {
      showError('Please fill in all required fields');
      return;
    }

    try {
      const secretId = editingSecret.id || `${editingSecret.category}_${editingSecret.key.toLowerCase().replace(/\s+/g, '_')}`;
      const secretRef = doc(db, 'system_secrets', secretId);
      
      const secretData = {
        key: editingSecret.key,
        value: editingSecret.value,
        description: editingSecret.description || '',
        category: editingSecret.category,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser?.email || 'unknown',
      };

      await setDoc(secretRef, secretData, { merge: true });
      showSuccess(`Secret ${editingSecret.key} saved successfully`);
      setIsEditing(false);
      setEditingSecret(null);
      fetchSecrets();
    } catch (error) {
      console.error('Error saving secret:', error);
      showError('Failed to save secret');
    }
  };

  const handleDelete = async (id: string, key: string) => {
    if (!window.confirm(`Are you sure you want to delete the secret "${key}"?`)) return;

    try {
      await deleteDoc(doc(db, 'system_secrets', id));
      showSuccess('Secret deleted successfully');
      fetchSecrets();
    } catch (error) {
      console.error('Error deleting secret:', error);
      showError('Failed to delete secret');
    }
  };

  const toggleValueVisibility = (id: string) => {
    setShowValues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderSecretForm = () => (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm`}>
      <div className={`w-full max-w-md p-6 rounded-2xl shadow-2xl ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} transition-all transform`}>
        <h3 className="text-xl font-bold mb-6">{editingSecret?.id ? 'Edit Secret' : 'Add New Secret'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1 opacity-70">Category</label>
            <select
              value={editingSecret?.category || ''}
              onChange={(e) => setEditingSecret({ ...editingSecret, category: e.target.value })}
              className={`w-full px-4 py-2.5 rounded-xl border appearance-none focus:ring-2 focus:ring-blue-500 outline-none transition-all ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <option value="">Select Category</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1 opacity-70">Secret Key Name</label>
            <input
              type="text"
              placeholder="e.g. AWS_ACCESS_KEY_ID"
              value={editingSecret?.key || ''}
              onChange={(e) => setEditingSecret({ ...editingSecret, key: e.target.value })}
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1 opacity-70">Secret Value</label>
            <textarea
              placeholder="Paste your key or secret here"
              value={editingSecret?.value || ''}
              onChange={(e) => setEditingSecret({ ...editingSecret, value: e.target.value })}
              rows={3}
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all font-mono text-sm ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1 opacity-70">Description (Optional)</label>
            <input
              type="text"
              placeholder="What is this key used for?"
              value={editingSecret?.description || ''}
              onChange={(e) => setEditingSecret({ ...editingSecret, description: e.target.value })}
              className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            />
          </div>
        </div>

        <div className="flex space-x-3 mt-8">
          <button
            onClick={() => { setIsEditing(false); setEditingSecret(null); }}
            className={`flex-1 py-2.5 rounded-xl font-semibold transition-all ${
              isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-blue-500/20"
          >
            Save Secret
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`p-8 min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">System Secrets & API Keys</h1>
            <p className={`mt-2 text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage your environment variables and third-party access keys securely.
            </p>
          </div>
          <button
            onClick={() => { setIsEditing(true); setEditingSecret({ category: 'aws' }); }}
            className="px-6 py-3 bg-black text-white dark:bg-white dark:text-black rounded-2xl font-bold transition-all hover:scale-105 shadow-xl flex items-center space-x-2"
          >
            <span>+ Add New Secret</span>
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="font-medium animate-pulse">Fetching your secrets...</p>
          </div>
        ) : secrets.length === 0 ? (
          <div className={`p-12 text-center rounded-3xl border-2 border-dashed ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="text-5xl mb-4">🔑</div>
            <h3 className="text-xl font-bold mb-2">No Secrets Found</h3>
            <p className="opacity-60 max-w-sm mx-auto mb-6">You haven't added any system secrets yet. Get started by adding your first key.</p>
            <button
              onClick={() => { setIsEditing(true); setEditingSecret({ category: 'aws' }); }}
              className="text-blue-500 font-bold hover:underline"
            >
              Add your first secret →
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {CATEGORIES.map(category => {
              const categorySecrets = secrets.filter(s => s.category === category.id);
              if (categorySecrets.length === 0) return null;

              return (
                <div key={category.id} className="space-y-4">
                  <div className="flex items-center space-x-2 px-1">
                    <span className="text-2xl">{category.icon}</span>
                    <h2 className="text-xl font-bold">{category.name}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                      {categorySecrets.length}
                    </span>
                  </div>

                  <div className={`rounded-3xl border overflow-hidden shadow-sm ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'bg-gray-800/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                          <th className="px-6 py-4">Key Name</th>
                          <th className="px-6 py-4">Value</th>
                          <th className="px-6 py-4 hidden md:table-cell">Last Updated</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/20">
                        {categorySecrets.map(secret => (
                          <tr key={secret.id} className={`group transition-colors ${isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'}`}>
                            <td className="px-6 py-5">
                              <div className="flex items-center space-x-2">
                                <div className="font-bold">{secret.key}</div>
                                {secret.isActive ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-green-500/20 text-green-500 font-bold uppercase tracking-tight">Active</span>
                                ) : (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-500/20 text-gray-500 font-bold uppercase tracking-tight">Secondary</span>
                                )}
                              </div>
                              <div className="flex items-center mt-1 space-x-2">
                                {secret.isSystemDefault ? (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded shadow-sm font-medium ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>System Default</span>
                                ) : (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded shadow-sm font-medium ${isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-600'}`}>Custom Override</span>
                                )}
                                {secret.description && (
                                  <span className="text-xs opacity-50 truncate max-w-[150px]">{secret.description}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex items-center space-x-3">
                                <code className={`px-2 py-1 rounded-lg text-sm font-mono break-all ${
                                  isDark ? 'bg-gray-800' : 'bg-gray-100'
                                }`}>
                                  {showValues[secret.id] ? secret.value : '••••••••••••••••'}
                                </code>
                                <button
                                  onClick={() => toggleValueVisibility(secret.id)}
                                  className="text-gray-500 hover:text-blue-500 transition-colors"
                                  title={showValues[secret.id] ? "Hide value" : "Show value"}
                                >
                                  {showValues[secret.id] ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                                  ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  )}
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-5 hidden md:table-cell">
                              {secret.isSystemDefault ? (
                                <div className="text-xs opacity-40">Static Config</div>
                              ) : (
                                <div className="text-sm">
                                  <div>{secret.updatedAt?.toDate().toLocaleDateString()}</div>
                                  <div className="text-xs opacity-40">by {secret.updatedBy}</div>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-5 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                {secret.isSystemDefault ? (
                                  <button
                                    onClick={() => { setIsEditing(true); setEditingSecret({ ...secret, id: undefined, value: '', isSystemDefault: false }); }}
                                    className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all"
                                    title="Override"
                                  >
                                    Override
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => { setIsEditing(true); setEditingSecret(secret); }}
                                      className="p-2 hover:bg-blue-500/10 hover:text-blue-500 rounded-lg transition-all"
                                      title="Edit"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDelete(secret.id, secret.key)}
                                      className="p-2 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-all"
                                      title="Delete"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`mt-12 p-6 rounded-3xl border ${isDark ? 'bg-blue-900/10 border-blue-900/20' : 'bg-blue-50 border-blue-100'}`}>
          <div className="flex items-start space-x-4">
            <span className="text-2xl">💡</span>
            <div>
              <h4 className="font-bold mb-1">How it works</h4>
              <p className="text-sm opacity-70 leading-relaxed">
                These secrets are stored in Firestore and fetched dynamically by the system. If a secret is found here, it will override the corresponding environment variable in your <code className="font-mono bg-white/10 px-1 rounded">.env</code> file. This allows you to update keys instantly without redeploying the application.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isEditing && renderSecretForm()}
    </div>
  );
};

export default AdminSecretsManager;
